import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ClipboardCheck, Pill, Play, Stethoscope } from "lucide-react";

import { medicalService, type Clinician, type ClinicLearner, type MedicalRecord, type MedicalVisit, type Medication, type Prescription } from "../../services/medicalService";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field } from "../FormLayout";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ClinicalAlerts, errorLine } from "./ClinicParts";
import { PrescriptionForm } from "./PrescriptionsPanel";
import { CompleteVisitForm, NewVisitForm } from "./VisitForms";
import { PRIORITIES, VISIT_TYPES, clock, day, isFever, isOpenVisit, labelFrom, prescriptionStatus, priorityDot, sortLearners, visitConfig, visitStatus, vitalsLine } from "./medicalLogic";

const when = (value?: string | null) => (value ? `${value.slice(0, 10)} ${value.slice(11, 16)}` : undefined);

/** The sick bay's queue: who is waiting, who is with the clinician, and what was found. */
export function VisitsPanel({ visits, records, learners, clinicians, medicines, prescriptions, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "follow-up" from the dashboard. */
  initialView?: string;
  visits?: MedicalVisit[];
  records: MedicalRecord[];
  learners: ClinicLearner[];
  clinicians: Clinician[];
  medicines: Medication[];
  prescriptions: Prescription[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [prescribing, setPrescribing] = useState(false);
  const [reason, setReason] = useState("");
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const start = useMutation({ mutationFn: medicalService.startVisit, onSuccess: (visit) => done(`${visit.student_name} is with the clinician now.`) });
  const cancel = useMutation({ mutationFn: medicalService.cancelVisit, onSuccess: (visit) => done(`${visit.student_name}'s visit was cancelled.`, true) });

  const columns: RegisterColumn<MedicalVisit>[] = [
    {
      header: "When",
      cell: (row) => <span className="tabular-nums">{row.visit_date === today ? "Today" : day(row.visit_date)} {clock(row.visit_time)}</span>,
      exportValue: (row) => `${day(row.visit_date)} ${clock(row.visit_time)}`,
      className: "whitespace-nowrap text-gray-700",
    },
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{row.student_id}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[10rem]",
    },
    { header: "Learner number", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "Complaint", cell: (row) => row.chief_complaint, exportValue: (row) => row.chief_complaint, className: "min-w-[14rem] max-w-md whitespace-normal text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(VISIT_TYPES, row.visit_type), exportValue: (row) => labelFrom(VISIT_TYPES, row.visit_type), className: "text-gray-700" },
    { header: "Priority", cell: (row) => <StatusDot label={labelFrom(PRIORITIES, row.priority)} dot={priorityDot(row.priority)} />, exportValue: (row) => labelFrom(PRIORITIES, row.priority) },
    { header: "Diagnosis", cell: (row) => row.diagnosis || "", exportValue: (row) => row.diagnosis ?? "", exportOnly: true },
    { header: "Seen by", cell: (row) => row.medical_staff_name, exportValue: (row) => row.medical_staff_name, className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...visitStatus(row, today)} />, exportValue: (row) => visitStatus(row, today).label },
  ];

  const visit = viewing !== null ? visits?.find((row) => row.id === viewing) : undefined;
  const record = visit ? records.find((row) => row.student === visit.student) : undefined;
  const learner = visit ? learners.find((row) => row.id === visit.student) : undefined;
  const fromVisit = visit ? prescriptions.filter((row) => row.medical_visit === visit.id) : [];
  const canPrescribe = canManage && visit && (visit.status === "in_progress" || visit.status === "completed");

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={`${visits ? "loaded" : "loading"}-${initialView ?? ""}`}
        title="Sick-bay visits"
        description={canManage ? "Open a visit to start it, write up what was found, prescribe or cancel it." : "Learners seen at the sick bay and what was found."}
        actions={canManage && (
          <button type="button" onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Stethoscope className="mr-1.5 h-3.5 w-3.5" /> New visit
          </button>
        )}
        rows={visits}
        config={visitConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          start.reset();
          cancel.reset();
          setReason("");
          setViewing(row.id);
        }}
        openLabel={(row) => (!canManage ? "Open" : row.status === "in_progress" ? "Complete" : row.status === "scheduled" && row.visit_date <= today ? "Start" : "Open")}
        noun={["visit", "visits"]}
        exportName={{ file: "sick-bay-visits", title: "Sick Bay Visits" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No visits yet. Record the first with New visit." : "No visits have been recorded."}
        initialState={initialView ? { view: initialView } : visits?.some((row) => row.visit_date === today) ? undefined : { view: "all" }}
      />

      {visit && !completing && !prescribing && (
        <DetailsModal
          title={`${visit.student_name} · ${labelFrom(VISIT_TYPES, visit.visit_type)}`}
          headline={{
            label: visit.status === "scheduled" && visit.visit_date > today ? "Booked for" : "Visit",
            value: `${visit.visit_date === today ? "Today" : day(visit.visit_date)} at ${clock(visit.visit_time)}`,
            note: visit.chief_complaint,
          }}
          status={visitStatus(visit, today)}
          notice={<ClinicalAlerts record={record} missingText="No health record for this learner, so allergies and conditions are unknown." />}
          facts={[
            ["Learner", `${visit.student_name} (${visit.student_id})`],
            ["Class", learner?.class_name ?? undefined],
            ["Priority", <StatusDot key="priority" label={labelFrom(PRIORITIES, visit.priority)} dot={priorityDot(visit.priority)} />],
            ["Seen by", visit.medical_staff_name],
            ["How long", visit.duration ?? undefined],
            ["Vital signs", vitalsLine(visit.vital_signs) ? <span key="vitals" className={isFever(visit.vital_signs) ? "text-red-700" : undefined}>{vitalsLine(visit.vital_signs)}</span> : undefined],
            ["Started", when(visit.started_at)],
            ["Completed", when(visit.completed_at)],
            ["Follow-up", visit.follow_up_required ? day(visit.follow_up_date) : undefined],
            ["Referred to", visit.referral_required ? visit.referral_to ?? undefined : undefined],
          ]}
          sections={[
            { title: "Symptoms", body: visit.symptoms },
            { title: "Examination", body: visit.physical_examination },
            { title: "Diagnosis or assessment", body: visit.diagnosis },
            { title: "Treatment", body: visit.treatment_plan },
            { title: "Procedures done", body: visit.procedures_performed },
            { title: "Follow-up: what to check", body: visit.follow_up_required ? visit.follow_up_notes : null },
            { title: "Why referred", body: visit.referral_required ? visit.referral_reason : null, tone: "warning" },
            { title: "Other notes", body: visit.notes },
            { title: "Why it was cancelled", body: visit.cancellation_reason, tone: "danger" },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canPrescribe && (
                <button type="button" onClick={() => setPrescribing(true)} className="btn btn-outline gap-1.5">
                  <Pill className="h-4 w-4" /> Prescribe
                </button>
              )}
              {canManage && visit.status === "scheduled" && visit.visit_date <= today && (
                <button type="button" disabled={start.isPending} onClick={() => start.mutate(visit.id)} className="btn btn-primary gap-1.5">
                  <Play className="h-4 w-4" /> {start.isPending ? "Starting…" : "Start the visit"}
                </button>
              )}
              {canManage && visit.status === "in_progress" && (
                <button type="button" onClick={() => setCompleting(true)} className="btn btn-primary gap-1.5">
                  <ClipboardCheck className="h-4 w-4" /> Complete the visit
                </button>
              )}
            </>
          }
        >
          {canManage && visit.status === "scheduled" && visit.visit_date > today && (
            <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">It can be started on {day(visit.visit_date)}.</p>
          )}
          {start.isError && errorLine(start.error, "The visit could not be started.")}

          {fromVisit.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Prescribed at this visit</h4>
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {fromVisit.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span>
                      <span className="font-medium text-gray-900">{row.medication_name}</span>
                      <span className="text-gray-600"> · {row.dosage}, {row.frequency}, {row.duration}</span>
                    </span>
                    <StatusDot {...prescriptionStatus(row)} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {canManage && isOpenVisit(visit) && (
            <ActionPanel title="Cancel the visit" description="For a learner who did not come or went back to class. Cancel any prescription from it first.">
              <Field label="Why it is cancelled" as="div">
                <AIAssistField
                  rows={2}
                  value={reason}
                  onChange={(next) => setReason(next)}
                  placeholder="e.g. The learner felt better and went back to class."
                  context={{ key: "medical.cancellation_reason", params: { kind: "visit", id: visit.id } }}
                  draftLabel="Draft the reason"
                  draftHint="Jot why first, and it writes it up. It sees the booking, never the learner's health details."
                />
              </Field>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!reason.trim() || cancel.isPending}
                  onClick={() => cancel.mutate({ id: visit.id, reason: reason.trim() })}
                  className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Cancel the visit
                </button>
              </div>
              {cancel.isError && errorLine(cancel.error, "The visit could not be cancelled.")}
            </ActionPanel>
          )}
        </DetailsModal>
      )}

      {visit && completing && (
        <CompleteVisitForm
          visit={visit}
          onClose={() => setCompleting(false)}
          onDone={(saved) => {
            setCompleting(false);
            done(`${saved.student_name}'s visit is complete.${saved.follow_up_required ? ` Follow-up on ${day(saved.follow_up_date)}.` : ""} Prescribe from it if medicine is needed.`);
          }}
        />
      )}

      {visit && prescribing && (
        <PrescriptionForm
          visit={visit}
          visits={visits ?? []}
          records={records}
          medicines={medicines}
          onClose={() => setPrescribing(false)}
          onDone={(row) => {
            setPrescribing(false);
            done(`${row.medication_name} written for ${row.student_name}. Dispense it from Prescriptions when you hand it over.`);
          }}
        />
      )}

      {adding && (
        <NewVisitForm
          learners={sortLearners(learners)}
          records={records}
          clinicians={clinicians}
          onClose={() => setAdding(false)}
          onDone={(saved, text) => {
            setAdding(false);
            setNotice({ tone: "success", text });
            onChanged();
            if (saved.status === "in_progress") setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
